import { Box, Grid, MenuItem, Typography } from '@mui/material'
import { Modal } from 'react-bootstrap'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import ButtonCustom from 'component/Button/ButtonCustom'
import InputTextarea from 'component/Input/InputTextarea'
import Info from 'features/finance/payment/component/popupPaymentTemp/Info'
import PaymentChannels from 'features/finance/payment/component/popupPaymentTemp/PaymentChannels'
import SummaryReceipt from 'features/finance/payment/component/popupPaymentTemp/SummaryReceipt'

/** CONSTANTS */
import { colors } from 'constants/theme'


/** STYLE */
import { fileUrl } from 'api/api'
import { ApprovalStatus } from 'api/approval/approval.api'
import PaymentApi from 'api/counter/payment/payment.api'
import RegisterApi from 'api/counter/register.api'
import TreatmentSummaryApi from 'api/dentists/treatment-summary.api'
import OverdueApi from 'api/finances/overdue.api'
import PatientTypeApi from 'api/setting/default-users/patient-type.api'
import { setConcurrencyToken, treatment } from 'app/slice/treatment.slice'
import { swalCustom } from 'component/Alert/Swal'
import Loading from 'component/Loading'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import BasicSelect from 'component/Select/BasicSelect'
import { Config } from 'config'
import * as UseStyled from 'features/finance/payment/component/popupPaymentTemp/UseStyledFormPayment'
import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'
import { getBranch, getClinicInfo, getCrm, getMember, numberFormat, numberToCurrencyFormat } from 'utils/app.utils'
import { getItem as getSecureItem, setItem as setSecureItem } from 'utils/secureStorage'

import { dateTimeToView } from 'utils/date.utils'
import PaymentModals from './components/PaymentModals'
import { useIndex } from './hooksTemp/useIndex'
import { ObjPaymentProps } from './types/types'
import { getItemType } from './utils/paymentUtils'

type ModalCustomProps = {
  systemConfig: {
    menu: 'PAYMENT' | 'OVERDUE' | 'RETROSPECTIVE' | 'EDIT_PAYMENT'
    isUseCrm: boolean
    isUseNhso: boolean
    patientCaseId: number
  }
  isShow: boolean | false
  patient: any
  detail?: any
  overdueDetail?: any
  histories?: any[]
  orderPaymentId?: number
  initBalance?: any
  setIsShow: (show: boolean) => void
  onSuccess?: (val?: any) => void
  date?: any
  dataPatientCase?: any
  outStandingPayment?: string;
  onCancelSuccess?: () => void
}

export interface PatientFinanceInterface {
  advanceBalance: number
  outstandingBalance: number
  treatmentRights: any[]
}

export interface PaidItem {
  value: number
  balance: number
  treatmentPrice: number
  itemKey: string
  id: number
  type: string
  itemType: string
  itemId: number
  qty: number
  price: number
  paid?: number
}

export interface ItemChannel {
  checked: boolean
  disabledChecked: boolean
  disabledInput: boolean
  value: number
}

export interface PaidChannel {
  checked: boolean
  disabledChecked: boolean
  disabledInput: boolean
  amountPaid: number
  itemChannel: Record<string, ItemChannel>
}
export interface TreatmentRight {
  checked: boolean
  disabledChecked: boolean
  disabledInput: boolean
  amountPaid: number
  itemChannel: Record<string, ItemChannel>
}

export interface ListOrderPayment {
  paid: Record<string, PaidItem>
  paidChannel: Record<string | number, PaidChannel>
  treatmentRight: any | null
  promotion: any | null
  /** หลาย promotion keyed by promotionBranchId */
  promotions?: Record<number, any>
  voucher?: any | null
  /** หลาย voucher keyed by voucherListId */
  vouchers?: Record<number, any>
}

export default function PopupPayment(props: ModalCustomProps) {
  const { t } = useTranslation()
  const clinicInfo = useMemo(() => getClinicInfo(), [])


  const autoIncrement = useMemo(() => clinicInfo.autoIncrement, [clinicInfo.autoIncrement])
  const memberAll = useMemo(() => getMember(), [])
  const isUseCrm = useMemo(() => getCrm(), [getCrm])
  const branchIdMemo = useMemo(() => getBranch(), [])
  const history = useHistory()
  const dispatch = useDispatch()

  const treatments = useSelector(treatment)

  const [activePage, setActivePage] = useState(0)
  const [loading, setLoading] = useState(false)

  const initialObjPayment = useMemo(() => ({
    totalPrice: 0,
    overdue: 0,
    approvedArrear: 0,
    balance: 0,
    discountPromotion: 0,
    discountVoucher: 0,
    discountTreatmentRight: 0,
    advance: 0,
    amountReceipt: 0,
    amountChange: 0,
    prefix: '',
    remark: ''
  }), [])
  const [objPayment, setObjPayment] = useState<ObjPaymentProps>(initialObjPayment)

  // ✅ OPTIMIZED: Memoize ช่องทางชำระเงินเริ่มต้น
  const initialPaymentChannels = useMemo(() => {
    return {
      5: { key: 'TREATMENTRIGHT', paymentChanel: 5, isActive: false, channelIndex: null, isDisabled: false, sorting: 0 },
      6: { key: 'ADVANCE', paymentChanel: 6, isActive: false, channelIndex: null, isDisabled: false, sorting: 1 },
      1: { key: 'CASH', paymentChanel: 1, isActive: false, channelIndex: null, isDisabled: false, sorting: 2 },
      2: { key: 'TRANSFER', paymentChanel: 2, isActive: false, channelIndex: null, isDisabled: false, bank: null, sorting: 3 },
      3: { key: 'CREDIT', paymentChanel: 3, isActive: false, channelIndex: null, isDisabled: false, bank: null, sorting: 4 },
      4: { key: 'DEBIT', paymentChanel: 4, isActive: false, channelIndex: null, isDisabled: false, bank: null, sorting: 5 },
      7: { key: 'VOUCHER', paymentChanel: 7, isActive: false, channelIndex: null, isDisabled: false, sorting: 6 },
      8: { key: 'PROMOTION', paymentChanel: 8, isActive: false, channelIndex: null, isDisabled: false, sorting: 7 },
      10: { key: 'WALLET', paymentChanel: 10, isActive: false, channelIndex: null, isDisabled: false, sorting: 8 }
    }
  }, [])

  const initialListOrderPayment = useMemo(() => {
    return {
      paid: {},
      paidChannel: {},
      treatmentRight: null,
      promotion: {},
      promotions: {} as Record<number, any>,
      voucher: {},
      vouchers: {} as Record<number, any>
    }
  }, [])
  const [listOrderPayment, setListOrderPayment] = useState<ListOrderPayment>(initialListOrderPayment)
  /** ตัวเลือก (OPTIONS) */
  const [selectdChannel, setSelectdChannel] = useState(initialPaymentChannels)

  // Payment Modals states
  const [configModal, setConfigModal] = useState<any>({})
  const [openModal, setOpenModal] = useState(false)
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [isNhsoSaveMedicalModal, setIsNhsoSaveMedicalModal] = useState(false)
  const [isCheckErrorNhsoModal, setiIsCheckErrorNhsoModal] = useState(false)
  const [isDupicate, setIsDupicate] = useState(false)
  const [checkDataError, setCheckDataError] = useState<any[]>([])
  const [checkType, setCheckType] = useState<any[]>([])
  const [dataUrl, setDataUrl] = useState<any>(null)
  const [objectData, setObjectData] = useState<any>(null)
  const [currentRank, setCurrentRank] = useState<any>(null)
  const [newRank, setNewRank] = useState<any>(null)
  const [disabledNext, setDisabledNext] = useState<boolean>(false)
  const [disabledButton, setDisabledButton] = useState<boolean>(false)
  // Use the extracted controller logic
  const {
    bankTransfers,
    bankCredits,
    bankDebits,
    patientFinance,
    billPrefixes,
    listOrder,
    defaultPaymentChannels,
    promotionList,
    voucherList,
  } = useIndex({ ...props, autoIncrement })

  const sortedListOrder = useMemo(() => {
    if (!Array.isArray(listOrder)) return []

    return [...listOrder].sort((a: any, b: any) => {
      const codeA = String(a?.operativeCode || a?.itemCode || '').trim()
      const codeB = String(b?.operativeCode || b?.itemCode || '').trim()
      return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' })
    })
  }, [listOrder])

  const priceOutStanding = useMemo(() =>
    treatments?.overdueStatus === ApprovalStatus.APPROVED ? treatments?.outStandingPayment?.overdueTotal || 0 : props.outStandingPayment === '1' && props.detail ? props.detail?.overdue || 0 : 0,
    [treatments?.overdueStatus, treatments?.outStandingPayment?.overdueTotal, props.detail]
  )

  useEffect(() => {
    if (!props.isShow) {
      return
    }

    setListOrderPayment(initialListOrderPayment)
  }, [props.isShow])



  useEffect(() => {
    if (!props.isShow) {
      return
    }

    if (props.systemConfig.menu === 'EDIT_PAYMENT' && props.detail) {

      const { detail: { orderPaymentChannels } } = props
      // setListOrderPayment(props.detail)
      const newChannels: any = { ...initialPaymentChannels }
      let channelIndex = 1
      const countChannel: any = {}
      Object.keys(orderPaymentChannels).forEach((key: any) => {
        const channel = orderPaymentChannels[key]
        let bank: any = null
        if (channel.bankId) {
          bank = bankTransfers.find((b: any) => b.bankId.toString() === channel.bankId.toString()) || null
          if (!bank) bank = bankCredits.find((b: any) => b.bankId.toString() === channel.bankId.toString()) || null
          if (!bank) bank = bankDebits.find((b: any) => b.bankId.toString() === channel.bankId.toString()) || null
          if (bank) bank = { ...bank, cardTypeId: channel.cardTypeId }
        }
        if (!countChannel[channel.paymentChannelId]) {
          if (newChannels[channel.paymentChannelId]) {
            newChannels[channel.paymentChannelId] = { ...newChannels[channel.paymentChannelId], isActive: true, channelIndex: channelIndex++, bank: bank }
          }
          countChannel[channel.paymentChannelId] = channel.paymentChannelId
        }
      })
      setSelectdChannel(newChannels)
    }
    else {
      // ✅ Auto-select TREATMENTRIGHT channel for NHSO
      const channelsToSet: any = { ...initialPaymentChannels }
      if (!props.detail && props.systemConfig.isUseNhso && patientFinance.treatmentRights && patientFinance.treatmentRights.length > 0) {
        const treatmentRightChannel = channelsToSet[5] // TREATMENTRIGHT channel
        if (!treatmentRightChannel?.isActive) {
          channelsToSet[5] = { ...treatmentRightChannel, isActive: true, channelIndex: 1, isDisabled: true }
        }
      }

      setSelectdChannel(channelsToSet)
      const lengthPaymentSelect = _.filter(channelsToSet, (fs) => fs.isActive === true).length
      if (lengthPaymentSelect === 1) {
        const sl = _.find(channelsToSet, (fs) => fs.isActive === true)
        const defaultPrefix = _.get(_.find(defaultPaymentChannels, { paymentChannelId: Number(sl?.paymentChanel) }), 'defaultPrefix')
        if (defaultPrefix) {
          setObjPayment((prev) => ({ ...prev, prefix: defaultPrefix }))
        }
      }
    }

  }, [defaultPaymentChannels, props.isShow, initialPaymentChannels, props.detail, listOrder, bankCredits, bankDebits, bankTransfers, props.systemConfig.isUseNhso, patientFinance.treatmentRights])



  useEffect(() => {
    if (!props.isShow) {
      return
    }
    const lengthPaymentSelect = _.filter(selectdChannel, (fs) => fs.isActive === true).length
    if (lengthPaymentSelect === 1) {
      const sl = _.find(selectdChannel, (fs) => fs.isActive === true)
      const defaultPrefix = _.get(_.find(defaultPaymentChannels, { paymentChannelId: Number(sl?.paymentChanel) }), 'defaultPrefix')
      if (defaultPrefix) {
        setObjPayment((prev) => ({ ...prev, prefix: defaultPrefix }))
      }
    }

  }, [defaultPaymentChannels, props.isShow, selectdChannel])



  useEffect(() => {
    const totalPrice = _.sumBy(listOrder, (treatment: any) => {
      let itemTotal = 0
      if (treatment.installment) {
        const pendingInstallment = _.find(treatment.installment.installmentList, { status: "PENDING" });
        if (pendingInstallment) {
          itemTotal = pendingInstallment.price;
        }
      } else {
        itemTotal = treatment.total || 0
      }
      const itemDcc = parseFloat(treatment.totalDcc) || 0
      return itemTotal + itemDcc
    });


    setObjPayment((prev) => ({
      ...prev,
      totalPrice: totalPrice - priceOutStanding,
      overdue: 0,
      approvedArrear: priceOutStanding,
      balance: 0,
      discountPromotion: 0,
      discountVoucher: 0,
      discountTreatmentRight: 0,
      advance: 0,
      amountReceipt: 0,
      amountChange: 0
    }))
  }, [listOrder, priceOutStanding, props.detail])

  useEffect(() => {
    setListOrderPayment(initialListOrderPayment)

    const paid: any = {}
    const paidChannel: any = {}
    const chanelTreatmentRight: any = {}
    const itemChannel: any = {}
    const vouchers: Record<number, any> = {}
    const promotions: Record<number, any> = {}
    let promotion: any = {}
    let approvedArrear = objPayment.approvedArrear

    _.map(sortedListOrder, (treatment: any, index: number) => {
      const { type, id } = getItemType(treatment, isMenu, index)
      const itemType = treatment.operativeId ? "OPERATIVE" : treatment.group
      const itemId = treatment.operativeId || treatment.itemId
      const qty = treatment.qty
      const price = treatment.price

      let totalPrice = 0
      if (treatment.installment) {
        const pendingInstallment = _.find(treatment.installment.installmentList, { status: "PENDING" });
        if (pendingInstallment) {
          totalPrice = pendingInstallment.price;
        }
      } else {
        totalPrice = treatment.total || 0
      }

      const totalDcc = parseFloat(treatment.totalDcc) || 0
      const totalPriceWithDcc = totalPrice + totalDcc

      if (approvedArrear > 0) {
        if (approvedArrear > totalPriceWithDcc) {
          approvedArrear -= totalPriceWithDcc
          totalPrice = 0
        } else {
          totalPrice = totalPriceWithDcc - approvedArrear
          approvedArrear = 0
        }
      } else {
        totalPrice = totalPriceWithDcc
      }

      paid[`${type}_${id}`] = {
        value: 0,
        balance: totalPrice,
        treatmentPrice: totalPrice, itemKey: `${type}_${id}`,
        itemName: treatment.operativeName || treatment.itemName,
        itemCode: treatment.operativeCode || treatment.itemCode,
        id, type,
        itemType,
        itemId, qty, price
      }
      itemChannel[`${type}_${id}`] = { checked: false, disabledChecked: false, disabledInput: true, value: 0 }
    })

    for (const key of Object.keys(selectdChannel)) {
      paidChannel[key] = { checked: true, disabledChecked: false, disabledInput: true, amountPaid: 0, itemChannel }
    }

    if (props.detail) {
      const { detail: { orderPaymentChannels } } = props

      // หัก amountPaid จาก orderPayment อื่น (histories) ออกจาก paid[itemKey].paid
      // เพื่อให้ updateSummary คำนวณ balance per-item ได้ถูกต้อง
      // (ตรงกับสูตรใน updateSummary: treatmentPrice - paid - paidItem)
      // หมายเหตุ: ข้าม channel 8 (PROMOTION) เพราะ promotion จะถูกหักที่ total price
      // ผ่าน paided summation ที่ Index.tsx:880-882 อยู่แล้ว — ถ้านำมาใส่ที่ per-item ด้วย
      // จะเกิด double-subtraction
      if (props.systemConfig.menu === 'EDIT_PAYMENT') {
        const currentOrderPaymentId = props.detail.orderPaymentId
          ; (props.histories || []).forEach((history: any) => {
            if (history?.orderPaymentId === currentOrderPaymentId) return
              ; (history?.paidItems || []).forEach((channel: any) => {
                if (channel?.paymentChannelId === 8) return
                  ; (channel?.items || []).forEach((item: any) => {
                    const key = `${item.refItemType}_${item.refItemId}`
                    if (paid[key]) {
                      paid[key].paid = Number(paid[key]?.paid || 0) + Number(item.amountPaid || 0)
                    }
                  })
              })
          })
      }

      // for (const index in orderPaymentChannels) {

      for (const channelId in paidChannel) {
        let channel = orderPaymentChannels.filter((opc: any) => opc.paymentChannelId.toString() === channelId)

        if (Number(channelId) === 5 && channel.length) {

          const { treatmentRights } = patientFinance
          const updatedItemChannelMain: any = {}
          let totalAmountPaid = 0

          treatmentRights.forEach((treatmentRight: any) => {
            // ✅ FIX: Match by patientTreatmentRightId first, fallback to treatmentRightId for backward compatibility
            const useTreatmentRight = channel.find((item: any) => {
              if (item.patientTreatmentRightId) {
                return Number(item.patientTreatmentRightId) === Number(treatmentRight.patientTreatmentRightId)
              }
              // Fallback for old data
              return Number(item.treatmentRightId) === Number(treatmentRight.treatmentRightId)
            })

            const isChecked = useTreatmentRight ? true : false
            const newUpdatedItemChannel: any = {}
            let amountPaid = 0

            if (useTreatmentRight) {
              const { orderPaymentItems } = useTreatmentRight
              amountPaid = useTreatmentRight.amountPaid || 0
              totalAmountPaid += amountPaid
              orderPaymentItems.forEach((item: any) => {
                const { refItemId, refItemType, amountPaid: newAmountPaid } = item
                const itemKey = `${refItemType}_${refItemId}`
                newUpdatedItemChannel[itemKey] = { checked: true, disabledChecked: false, disabledInput: false, value: newAmountPaid }

                updatedItemChannelMain[itemKey] = { checked: true, disabledChecked: false, disabledInput: false, value: (updatedItemChannelMain[itemKey]?.value || 0) + newAmountPaid }

                // ✅ FIX: อัปเดต balance และ value ของ paid item
                if (paid[itemKey]) {
                  const currentBalance = paid[itemKey].balance ?? paid[itemKey].treatmentPrice
                  const currentValue = paid[itemKey].value || 0
                  paid[itemKey].balance = Math.max(0, currentBalance - newAmountPaid)
                  paid[itemKey].value = currentValue + newAmountPaid
                }
              })
            }

            chanelTreatmentRight[treatmentRight?.patientTreatmentRightId] = {
              ...treatmentRight,
              checked: isChecked,
              disabledChecked: false,
              disabledInput: true,
              amountPaid: amountPaid,
              itemChannel: newUpdatedItemChannel
            }

          })
          if (paidChannel[channelId]) {
            paidChannel[channelId] = { ...paidChannel[channelId], amountPaid: totalAmountPaid || 0, checked: true, disabledChecked: true, disabledInput: true, itemChannel: updatedItemChannelMain }
          }
        } else if (Number(channelId) === 7 && channel.length) {
          let totalVoucherAmount = 0
          channel.forEach((ch: any) => {
            const updatedItemChannel: Record<string, ItemChannel> = {}
            const amountPaidChannel = _.sumBy(ch.orderPaymentItems, (item: any) => item.amountPaid)
            totalVoucherAmount += amountPaidChannel
            _.map(sortedListOrder, (treatment: any, index: number) => {
              const { type, id } = getItemType(treatment, isMenu, index)
              const itemKey = `${type}_${id}`
              let amountPaid = 0
              const paided = ch.orderPaymentItems.find((item: any) => item.refItemId === id && item.refItemType === type)
              if (paided) amountPaid = paided.amountPaid
              updatedItemChannel[itemKey] = { checked: true, disabledChecked: true, disabledInput: true, value: amountPaid }
              if (paid[itemKey]) {
                const currentBalance = paid[itemKey].balance ?? paid[itemKey].treatmentPrice
                const currentValue = paid[itemKey].value || 0
                paid[itemKey].balance = Math.max(0, currentBalance - amountPaid)
                paid[itemKey].value = currentValue + amountPaid
              }
            })
            if (ch.voucherListId) {
              vouchers[ch.voucherListId] = {
                checked: true,
                voucherCode: ch?.voucherCode || '',
                voucherName: ch?.voucherName || '',
                voucherListId: ch.voucherListId,
                voucherId: ch?.voucherId || 999,
                detail: ch?.voucherDetail || '',
                amountPaid: ch.voucherPaid ?? amountPaidChannel,
                itemChannel: updatedItemChannel
              }
            }
          })
          if (paidChannel[channelId]) {
            const mergedItemChannel: Record<string, ItemChannel> = {}
            _.map(sortedListOrder, (treatment: any, index: number) => {
              const { type, id } = getItemType(treatment, isMenu, index)
              const itemKey = `${type}_${id}`
              let sumVal = 0
              Object.values(vouchers).forEach((v: any) => { sumVal += Number(v?.itemChannel?.[itemKey]?.value) || 0 })
              mergedItemChannel[itemKey] = { checked: sumVal > 0, disabledChecked: true, disabledInput: true, value: sumVal }
            })
            paidChannel[channelId] = { ...paidChannel[channelId], amountPaid: totalVoucherAmount, checked: true, disabledChecked: true, disabledInput: true, itemChannel: mergedItemChannel }
          }
        } else if (Number(channelId) === 8 && channel.length) {
          let totalPromotionAmount = 0
          channel.forEach((ch: any) => {

            const updatedItemChannel: Record<string, ItemChannel> = {}
            const amountPaidChannel = _.sumBy(ch.orderPaymentItems, (item: any) => item.amountPaid)
            totalPromotionAmount += amountPaidChannel
            _.map(sortedListOrder, (treatment: any, index: number) => {
              const { type, id } = getItemType(treatment, isMenu, index)
              const itemKey = `${type}_${id}`
              let amountPaid = 0
              const paided = ch.orderPaymentItems.find((item: any) => item.refItemId === id && item.refItemType === type)
              if (paided) amountPaid = paided.amountPaid

              /*** fix กรณี ใช้ promotion แค่หน้าการรักษา */
              if (props.systemConfig.menu === 'EDIT_PAYMENT') amountPaid = 0


              updatedItemChannel[itemKey] = { checked: true, disabledChecked: true, disabledInput: true, value: amountPaid }
              if (paid[itemKey]) {
                const currentBalance = paid[itemKey].balance ?? paid[itemKey].treatmentPrice
                const currentValue = paid[itemKey].value || 0
                paid[itemKey].balance = Math.max(0, currentBalance - amountPaid)
                paid[itemKey].value = currentValue + amountPaid
              }
            })
            if (ch.promotionBranchId) {
              promotions[ch.promotionBranchId] = {
                checked: true,
                promotionName: `${ch?.promotionName || ''}`,
                promotionBranchId: ch.promotionBranchId,
                promotionId: ch?.promotionId,
                detail: ch?.promotionDetail || '',
                amountPaid: ch.promotionPaid ?? amountPaidChannel,
                itemChannel: updatedItemChannel
              }
            }
          })
          if (paidChannel[channelId]) {
            const mergedItemChannel: Record<string, ItemChannel> = {}
            _.map(sortedListOrder, (treatment: any, index: number) => {
              const { type, id } = getItemType(treatment, isMenu, index)
              const itemKey = `${type}_${id}`
              let sumVal = 0
              Object.values(promotions).forEach((p: any) => { sumVal += Number(p?.itemChannel?.[itemKey]?.value) || 0 })
              mergedItemChannel[itemKey] = { checked: sumVal > 0, disabledChecked: true, disabledInput: true, value: sumVal }
            })
            paidChannel[channelId] = { ...paidChannel[channelId], amountPaid: totalPromotionAmount, checked: true, disabledChecked: true, disabledInput: true, itemChannel: mergedItemChannel }
          }
        } else {

          channel = channel.length ? channel[0] : null;
          const updatedItemChannel: Record<string, ItemChannel> = {}
          if (channel) {
            const isDisabled = channel?.promotionId || channel?.voucherListId ? true : false

            const amountPaidChannel = _.sumBy(channel.orderPaymentItems, (item: any) => item.amountPaid)
            _.map(sortedListOrder, (treatment: any, index: number) => {
              const { type, id } = getItemType(treatment, isMenu, index)
              const itemKey = `${type}_${id}`

              let amountPaid = 0
              const paided = channel.orderPaymentItems.find((item: any) => item.refItemId === id && item.refItemType === type)
              if (paided) amountPaid = paided.amountPaid

              updatedItemChannel[itemKey] = { checked: true, disabledChecked: isDisabled, disabledInput: isDisabled, value: amountPaid }

              if (paid[itemKey]) {
                const currentBalance = paid[itemKey].balance ?? paid[itemKey].treatmentPrice
                const currentValue = paid[itemKey].value || 0
                paid[itemKey].balance = Math.max(0, currentBalance - amountPaid)
                paid[itemKey].value = currentValue + amountPaid
              }
            })

            if (channel?.promotionId) {
              promotion = {
                checked: true,
                promotionName: `${channel?.promotionName || ''}`,
                promotionBranchId: channel?.promotionBranchId,
                promotionId: channel?.promotionId,
                detail: channel?.promotionDetail || '',
                itemChannel: updatedItemChannel
              }
              promotions[channel.promotionBranchId] = promotion
            }

            if (channel.voucherListId) {
              vouchers[channel.voucherListId] = {
                checked: true,
                voucherCode: channel?.voucherCode || '',
                voucherName: `${channel?.voucherName}`,
                voucherListId: channel?.voucherListId,
                voucherId: channel?.voucherId || 999,
                detail: channel?.voucherDetail || '',
                amountPaid: channel.voucherPaid ?? amountPaidChannel,
                itemChannel: updatedItemChannel
              }
            }
            if (paidChannel[channelId]) paidChannel[channelId] = { ...paidChannel[channelId], amountPaid: amountPaidChannel, checked: true, disabledChecked: isDisabled, disabledInput: isDisabled, itemChannel: updatedItemChannel }
          }

        }
      }
    } else if (props.systemConfig.menu === 'OVERDUE') {

      const histories = props.histories || []
      const newChannels: any = { ...initialPaymentChannels }
      histories.forEach((history: any) => {
        history.itemBalances.forEach((item: any) => {
          const key = `${item.refItemType}_${item.refItemId}`
          if (paid[key]) {
            paid[key].balance = Number(paid[key].treatmentPrice) - Number(item.paid)
            paid[key].paid = Number(paid[key]?.paid || 0) + Number(item.paid)
          }
        })
      })
    }
    // ✅ Auto-select first treatment right and set default amount for NHSO
    if (!props.detail && props.systemConfig.isUseNhso && patientFinance.treatmentRights && patientFinance.treatmentRights.length > 0) {
      // Auto-select first treatment right and set default amount
      const firstTreatmentRight = patientFinance.treatmentRights[0]
      if (firstTreatmentRight) {
        const totalBalance = Object.values(paid).reduce((sum: number, item: any) => sum + (item.balance || 0), 0)

        // Default amount is 700 or total balance (whichever is smaller)
        const defaultAmount = Math.min(700, totalBalance)

        if (defaultAmount > 0) {
          // Distribute amount across items
          const updatedItemChannel: Record<string, any> = {}
          let remainingAmount = defaultAmount
          let totalAmountPaid = 0

          Object.keys(paid).forEach((itemKey) => {
            const currentBalance = Number(paid[itemKey]?.balance || 0)

            let itemAmount = 0
            if (remainingAmount > 0 && currentBalance > 0) {
              itemAmount = Math.min(remainingAmount, currentBalance)
              remainingAmount -= itemAmount
              totalAmountPaid += itemAmount
            }

            const treatmentPrice = Number(paid[itemKey]?.treatmentPrice || 0)
            const newValue = itemAmount
            const newBalance = Math.max(0, treatmentPrice - newValue)

            updatedItemChannel[itemKey] = {
              checked: itemAmount > 0,
              disabledChecked: false,
              disabledInput: true,
              value: itemAmount
            }
            paid[itemKey] = {
              ...paid[itemKey],
              value: newValue,
              balance: newBalance
            }
          })

          chanelTreatmentRight[firstTreatmentRight.patientTreatmentRightId] = {
            ...firstTreatmentRight,
            checked: true,
            disabledChecked: true,
            disabledInput: true,
            amountPaid: totalAmountPaid,
            itemChannel: updatedItemChannel
          }

          if (paidChannel[5]) {
            paidChannel[5] = {
              ...paidChannel[5],
              checked: true,
              disabledChecked: true,
              disabledInput: true,
              amountPaid: totalAmountPaid,
              itemChannel: updatedItemChannel
            }
          }
        }
      }
    }


    setListOrderPayment((prev: any) => updateSummary({ ...prev, paid, paidChannel, vouchers, promotions, promotion, treatmentRight: chanelTreatmentRight }))
  }, [sortedListOrder, props.detail, patientFinance, props.systemConfig.isUseNhso])

  const getPaidItemBenefit = (itemChannel: any, paidItem: any) => {
    const paidItemBenefit: any = {}

    let amountPaid = 0

    for (const keyItem of Object.keys(itemChannel)) {
      if (!paidItem[keyItem]) paidItem[keyItem] = 0
      const item = itemChannel[keyItem]
      if (!paidItemBenefit[keyItem]) paidItemBenefit[keyItem] = { value: 0, checked: false }
      if (item.checked) {
        paidItem[keyItem] += Number(item.value)
        paidItemBenefit[keyItem].value += Number(item.value)
        paidItemBenefit[keyItem].checked = true
        amountPaid += Number(item.value)
      }
    }

    return { paidItem, paidItemBenefit, amountPaid }
  }

  const updateSummary = (next: any) => {
    const { paid, paidChannel, treatmentRight, promotion, promotions, voucher, vouchers } = next;
    const vouchersMap = vouchers && Object.keys(vouchers).length ? vouchers : (voucher?.voucherListId ? { [voucher.voucherListId]: voucher } : {});
    const promotionsMap = promotions && Object.keys(promotions).length ? promotions : (promotion?.promotionBranchId ? { [promotion.promotionBranchId]: promotion } : {});

    const channelBenifit = [5, 7, 8]
    const paidItem: any = {}
    const balanceItem: any = {}
    const paidItemTreatmentRight: any = {}
    const paidItemBenefit: any = {}
    if (treatmentRight)
      Object.keys(treatmentRight).forEach((key: any) => {
        const itemChannel = treatmentRight[key].itemChannel ?? {};
        for (const keyItem of Object.keys(itemChannel)) {
          if (!paidItem[keyItem]) paidItem[keyItem] = 0
          const item = itemChannel[keyItem]
          if (!paidItemTreatmentRight[keyItem]) paidItemTreatmentRight[keyItem] = { value: 0, checked: false }
          if (item.checked) {
            paidItem[keyItem] += Number(item.value)
            paidItemTreatmentRight[keyItem].value += Number(item.value)
            paidItemTreatmentRight[keyItem].checked = true
          }
        }
      })
    if (Object.keys(promotionsMap).length) {
      const mergedBenefit8: any = {}
      Object.values(promotionsMap).forEach((p: any) => {
        if (!p?.promotionBranchId) return
        const itemChannel = p.itemChannel ?? {}
        const { paidItem: nextPaidItem, paidItemBenefit: item, amountPaid } = getPaidItemBenefit(itemChannel, paidItem)
        p.amountPaid = amountPaid
        Object.keys(item || {}).forEach((keyItem) => {
          if (!mergedBenefit8[keyItem]) mergedBenefit8[keyItem] = { value: 0, checked: false }
          mergedBenefit8[keyItem].value += Number(item[keyItem]?.value) || 0
          if (item[keyItem]?.checked) mergedBenefit8[keyItem].checked = true
        })
      })
      paidItemBenefit[8] = mergedBenefit8
    }
    if (Object.keys(vouchersMap).length) {
      const mergedBenefit7: any = {}
      Object.values(vouchersMap).forEach((v: any) => {
        if (!v?.voucherListId) return
        const itemChannel = v.itemChannel ?? {}
        const { paidItem: nextPaidItem, paidItemBenefit: item, amountPaid } = getPaidItemBenefit(itemChannel, paidItem)
        v.amountPaid = amountPaid
        Object.keys(item || {}).forEach((keyItem) => {
          if (!mergedBenefit7[keyItem]) mergedBenefit7[keyItem] = { value: 0, checked: false }
          mergedBenefit7[keyItem].value += Number(item[keyItem]?.value) || 0
          if (item[keyItem]?.checked) mergedBenefit7[keyItem].checked = true
        })
      })
      paidItemBenefit[7] = mergedBenefit7
    }

    Object.keys(paidChannel).forEach((key: any) => {

      if (!channelBenifit.includes(Number(key))) {
        const itemChannel = paidChannel[key].itemChannel ?? {};
        for (const keyItem of Object.keys(itemChannel)) {
          if (!paidItem[keyItem]) paidItem[keyItem] = 0
          const item = itemChannel[keyItem]
          if (item.checked) {
            paidItem[keyItem] += Number(item.value)
          } else {
            paidItem[keyItem] += 0
          }
        }
      } else {

        const itemChannel = paidChannel[key].itemChannel ?? {};
        if (Number(key) === 5 && itemChannel && treatmentRight) {
          // ✅ FIX: คำนวณ amountPaid จาก treatmentRight ทั้งหมด
          let totalTreatmentRightAmount = 0
          for (const keyItem of Object.keys(itemChannel)) {
            const item = itemChannel[keyItem]
            const { value, checked } = paidItemTreatmentRight?.[keyItem] ?? { ...item }
            /** รายการในช่องทางที่ 5 จ่ายเท่าไหร่ */
            paidChannel[key].itemChannel[keyItem] = { ...item, value: value, checked: checked }
          }

          // ✅ FIX: คำนวณยอดรวมจาก treatmentRight ทั้งหมด
          Object.keys(treatmentRight).forEach((trKey: any) => {
            const tr = treatmentRight[trKey]
            if (tr && tr.checked) {
              const trAmount = _.sumBy(Object.values(tr.itemChannel || {}), (it: any) =>
                it.checked ? Number(it?.value) || 0 : 0
              )
              totalTreatmentRightAmount += trAmount
            }
          })

          paidChannel[key].amountPaid = totalTreatmentRightAmount
          paidChannel[key].checked = totalTreatmentRightAmount > 0
        } else if (Number(key) === 8 && Object.keys(promotionsMap).length) {
          let paidPromotion = 0
          const mergedItemChannel8 = paidChannel[key].itemChannel ?? {}
          for (const keyItem of Object.keys(mergedItemChannel8)) {
            const { value, checked } = paidItemBenefit[8]?.[keyItem] ?? { value: 0, checked: false }
            paidChannel[key].itemChannel[keyItem] = { ...mergedItemChannel8[keyItem], value, checked }
            paidPromotion += Number(value)
          }
          paidChannel[key].amountPaid = paidPromotion
          paidChannel[key].checked = paidPromotion > 0
          paidChannel[key].disabledInput = true
        } else if (Number(key) === 7 && Object.keys(vouchersMap).length) {
          let paidVoucher = 0
          const mergedItemChannel = paidChannel[key].itemChannel ?? {}
          for (const keyItem of Object.keys(mergedItemChannel)) {
            const { value, checked } = paidItemBenefit[7]?.[keyItem] ?? { value: 0, checked: false }
            paidChannel[key].itemChannel[keyItem] = { ...mergedItemChannel[keyItem], value, checked }
            paidVoucher += Number(value)
          }
          paidChannel[key].amountPaid = paidVoucher
          paidChannel[key].checked = paidVoucher > 0
          paidChannel[key].disabledInput = true
        } else {
          for (const keyItem of Object.keys(paidChannel[key].itemChannel)) {
            const item = paidChannel[key].itemChannel[keyItem]
            paidChannel[key].itemChannel[keyItem] = { ...item, value: 0, checked: false }
          }
          paidChannel[key].amountPaid = 0
          paidChannel[key].checked = false
          paidChannel[key].disabledInput = false

        }
      }
    })

    for (const itemKey in paid) {

      /** 
       *  ต้องการอัพเดตยอดที่จ่ายของรายการนั้นๆ
       * key = รหัสรายการ
       * paid[key] =  {balance: 0, treatmentPrice: 0, value: 0} รายละเอียดรายการ
       * paidItem = แต่ละช่องทางจ่ายเท่าไหร่
       */
      if (paid[itemKey]) {
        let treatmentPrice = Number(paid[itemKey].treatmentPrice || 0)
        if (props.systemConfig.menu === 'OVERDUE' || props.systemConfig.menu === 'EDIT_PAYMENT') {
          treatmentPrice = Number(paid[itemKey].treatmentPrice) - Number(paid[itemKey].paid || 0)
        }
        let balance = treatmentPrice - paidItem[itemKey]
        if (balance < 0) balance = 0
        paid[itemKey].value = paidItem[itemKey]
        paid[itemKey].balance = balance
      }
    }
    return {
      ...next,
      paid,
      paidChannel,
      promotion: next.promotion,
      promotions: next.promotions,
      voucher: next.voucher,
      vouchers: next.vouchers
    }
  }

  useEffect(() => {

    const paidChannel = listOrderPayment?.paidChannel
    const totalPaid = _.reduce(paidChannel, (sum, item) => sum + (item.checked ? item.amountPaid : 0), 0)
    let paided = 0
    if (props.systemConfig.menu === 'OVERDUE') {
      paided = Object.values(listOrderPayment.paid).reduce((sum: number, item: any) => sum + Number(item.paid || 0), 0)
    }

    if (props.systemConfig.menu === "EDIT_PAYMENT") {
      paided = Object.values(props?.histories || []).reduce((sum: number, item: any) => sum + (item.orderPaymentId !== props.detail.orderPaymentId ? Number(item.amountPaid || 0) : 0), 0)
    }


    const newTotalPrice = (objPayment.totalPrice - paided) + objPayment.approvedArrear
    let balance = (newTotalPrice - objPayment.approvedArrear) - totalPaid

    if (balance < 0) balance = 0



    /** DISCOUNT PROMOTION */
    let discountPromotion = 0

    if (paidChannel[8]) {
      discountPromotion = paidChannel[8]?.amountPaid || 0
    }
    /** DISCOUNT VOUCHER */
    let discountVoucher = 0
    if (paidChannel[7] && paidChannel[7]?.checked) {
      discountVoucher = paidChannel[7]?.amountPaid || 0
    }
    /** DISCOUNT TREATMENT RIGHT */
    let discountTreatmentRight = 0

    if (paidChannel[5] && paidChannel[5]?.checked) discountTreatmentRight = paidChannel[5]?.amountPaid || 0

    /** ✅ FIX: NHSO case - ใช้ค่าที่คำนวณมา แต่ cap ที่ 700 แทนการ hard-code */
    if (props.systemConfig.isUseNhso && selectdChannel[5]?.isActive) {
      discountTreatmentRight = Math.min(discountTreatmentRight, 700)
    }
    /** ADVANCE */
    let advance = 0
    if (paidChannel[6] && paidChannel[6]?.checked) advance = paidChannel[6]?.amountPaid || 0

    const amountReceipt = totalPaid - discountPromotion - discountVoucher - discountTreatmentRight - advance
    let amountReceiptMax = amountReceipt
    if (totalPaid > newTotalPrice) amountReceiptMax = newTotalPrice

    setObjPayment((prev: any) => ({
      ...prev,
      overdue: balance + objPayment.approvedArrear,
      balance: balance + amountReceipt,
      discountPromotion,
      discountVoucher,
      discountTreatmentRight,
      advance,
      amountReceipt
    }))
  }, [listOrderPayment?.paidChannel, listOrderPayment.promotion, listOrderPayment.promotions, listOrderPayment.voucher, listOrderPayment.vouchers, listOrderPayment.treatmentRight, objPayment.totalPrice, objPayment.approvedArrear, selectdChannel, props.systemConfig.isUseNhso])


  const alertTreantRights = async () => {
    const checkSelected = Object.values(selectdChannel).filter((item: any) => item.isActive)
    if (!checkSelected.length) return notiError(t('PAYMENT.FORM.PLS_SELECT_PAYMENT_CHANNEL'), undefined, undefined, () => {
      setDisabledButton(false)
      setDisabledNext(false)
    })
    else setActivePage(1)
  }

  const finishPayment = async () => {

    const { treatmentRight, promotion, promotions, voucher, vouchers, paidChannel, paid } = listOrderPayment
    const vouchersList = (vouchers && Object.keys(vouchers).length) ? vouchers : (voucher?.voucherListId ? { [voucher.voucherListId]: voucher } : {})
    const promotionsList = (promotions && Object.keys(promotions).length) ? promotions : (promotion?.promotionBranchId ? { [promotion.promotionBranchId]: promotion } : {})
    // ✅ FIX: Validate payment amounts consistency before submitting
    const roundMoney = (value: number): number => Math.round(value * 100) / 100
    const validatePaymentAmounts = (): { valid: boolean, errors: string[] } => {
      const errors: string[] = []
      const tolerance = 0.01


      // Check advance balance
      const advanceUsed = roundMoney(objPayment.advance || 0)
      if (advanceUsed > patientFinance.advanceBalance) {
        errors.push(`เงินล่วงหน้าเกินยอดคงเหลือ (ใช้ ${advanceUsed}, มี ${patientFinance.advanceBalance})`)
      }


      // Check negative values
      Object.entries(paidChannel).forEach(([channelId, value]: [string, any]) => {
        if (value < 0) {
          errors.push(`ช่องทาง ${channelId} มีค่าติดลบ: ${value}`)
        }
      })

      return { valid: errors.length === 0, errors }
    }

    const validation = validatePaymentAmounts()
    if (!validation.valid) {
      console.error('❌ Payment validation failed:', validation.errors)
      notiError(
        'พบความไม่สอดคล้องของข้อมูล:\n' + validation.errors.join('\n'),
        undefined,
        undefined,
        () => { setDisabledButton(false) }
      )
      return
    }

    const newOrderPaymentChannels: any = []

    const totalDcc = _.sumBy(listOrder, (item: any) => parseFloat(item?.totalDcc || 0)) || 0


    // Helper function to get DCC amount from listOrder
    const getDccFromListOrder = (itemId: any, itemType: string) => {
      let paidDcc = 0
      let foundItem: any = null
      const item = _.find(listOrder, (treatment: any, index: number) => {
        const { type, id } = getItemType(treatment, isMenu, index)

        return Number(id) === Number(itemId) && type === itemType
      })
      for (const his of props?.histories || []) {
        foundItem = _.find(his.itemBalances, (item: any) =>
          Number(item.refItemId) === Number(itemId) && item.refItemType === itemType
        )
        paidDcc += (foundItem?.paidDcc || 0)
      }
      return (parseFloat(item?.totalDcc) || 0) - paidDcc
    }

    // คำนวณ amountPaid และ amountReceipt โดยไม่รวมช่องทาง 5 (TREATMENT_RIGHT) และไม่รวม DCC
    // จะคำนวณใหม่หลังจากสร้าง orderPaymentChannels แล้ว
    if (!Object.values(paid).length) return notiError(t('TREATMENT_SUMMARY.MESSAGE.PLS_SELECT_TREATMENT'), undefined, undefined, () => { setDisabledButton(false) })
    /** CASH */

    const channelRequiredBank = ['TRANSFER', 'CREDIT', 'DEBIT']
    const channelRequiredCardType = ['CREDIT', 'DEBIT']
    // เก็บ remainingDccAmount แยกตาม itemKey เพื่อให้รายการเดียวกันหัก DCC ต่อเนื่องกันข้ามช่องทาง
    const remainingDccByItemKey: any = {}
    // Flag สำหรับตรวจสอบว่ามี validation error หรือไม่
    let hasValidationError = false
    // เรียงลำดับช่องทางตาม channelIndex ก่อน เพื่อให้ได้ลำดับที่ถูกต้องตามที่ผู้ใช้เลือก
    const sortedChannels = Object.values(selectdChannel)
      .filter((channel: any) => channel.isActive)
      .sort((a: any, b: any) => (a.channelIndex ?? 9999) - (b.channelIndex ?? 9999))

    sortedChannels.forEach((channel: any) => {
      const paymentChannelId = channel.paymentChanel
      const paidItems = paidChannel[paymentChannelId]
      let orderPaymentItems: any = []
      let amountPaid = 0
      let checkSelect = false
      if (channel.isActive) {
        const bank = channel?.bank || {}

        if (Number(paymentChannelId) === 5) {
          Object.keys(treatmentRight).forEach((trId: any) => {
            checkSelect = false
            amountPaid = 0
            let amountPaidWithoutDcc = 0
            let amountPaidDccOnly = 0
            orderPaymentItems = []
            const items = treatmentRight[trId].itemChannel

            Object.keys(items).forEach((itemKey: any) => {
              const item = items[itemKey]
              if (item.checked) {
                const { id, type } = paid[itemKey]
                // ดึง DCC จาก listOrder
                const itemValue = Number(item.value) || 0

                // ถ้ายังไม่มี remainingDccAmount สำหรับ itemKey นี้ ให้ใช้ dccAmount เต็มจำนวน
                if (remainingDccByItemKey[itemKey] === undefined) {
                  const itemDccAmount = getDccFromListOrder(id, type)
                  remainingDccByItemKey[itemKey] = itemDccAmount
                }

                // คำนวณ amountPaidDcc จาก remainingDccAmount (ใช้ได้ไม่เกินยอดที่จ่าย)
                const amountPaidDcc = Math.min(remainingDccByItemKey[itemKey], itemValue)

                // คำนวณ itemAmountPaid (ยอดที่จ่ายลบด้วย DCC)
                const itemAmountPaid = Math.max(0, itemValue - amountPaidDcc)

                // ลบ DCC ที่ใช้แล้วออกจาก remainingDccByItemKey
                remainingDccByItemKey[itemKey] -= amountPaidDcc

                orderPaymentItems.push({
                  refItemId: id,
                  refItemType: type,
                  amountPaid: itemAmountPaid,
                  amountPaidDcc: amountPaidDcc
                })
                amountPaid += Number(item.value)
                amountPaidWithoutDcc += itemAmountPaid
                amountPaidDccOnly += amountPaidDcc
                checkSelect = true
              }
            })
            if (checkSelect) newOrderPaymentChannels.push({ paymentChannelId: paymentChannelId, amountPaid, bankId: '', cardTypeId: '', patientTreatmentRightId: Number(trId), orderPaymentItems })
          })
        } else if (Number(paymentChannelId) === 7 && Object.keys(vouchersList).length) {
          Object.values(vouchersList).forEach((v: any) => {
            if (!v?.voucherListId) return
            const orderPaymentItems: any = []
            let amountPaidSum = 0
            const itemChannel = v.itemChannel ?? {}
            const usage = v?.benefitUsage || v?.voucherUsage || 'ALL'
            Object.keys(itemChannel).forEach((itemKey: any) => {
              const item = itemChannel[itemKey]
              if (item?.checked && paid[itemKey]) {
                const { id, type } = paid[itemKey]
                const itemValue = Number(item.value) || 0
                const paymentItem: any = { refItemId: id, refItemType: type, amountPaid: itemValue }
                if (['OPERATIVE', 'OPERATIVEDF', 'OPERATIVECF'].includes(usage)) {
                  paymentItem.amountPaidDF = Number(item.dfAmount) || 0
                  paymentItem.amountPaidCF = Number(item.cfAmount) || 0
                }
                orderPaymentItems.push(paymentItem)
                amountPaidSum += itemValue
              }
            })
            newOrderPaymentChannels.push({
              paymentChannelId: 7,
              amountPaid: amountPaidSum,
              bankId: '',
              cardTypeId: '',
              orderPaymentItems,
              patientTreatmentRightId: null,
              promotionPaid: 0,
              promotionBranchId: 0,
              promotionId: 0,
              voucherPaid: v?.amountPaid || 0,
              voucherListId: v.voucherListId,
              voucherUsage: usage,
            })
          })
        } else if (Number(paymentChannelId) === 8 && Object.keys(promotionsList).length) {

          Object.values(promotionsList).forEach((p: any) => {

            if (!p?.promotionBranchId) return


            if (props.systemConfig.menu === "EDIT_PAYMENT") {
              const orderPaymentItems: any = []
              let amountPaidSum = 0
              const itemChannel = p.itemChannel ?? {}
              const usage = p?.benefitUsage || p?.promotionUsage || 'ALL'
             const oldPaid= _.find(props.detail.orderPaymentChannels, { paymentChannelId: 8 ,promotionBranchId:p?.promotionBranchId})

             console.log('oldPaid',oldPaid)

              oldPaid.orderPaymentItems.forEach((value: any, index:any) => {
                  const itemValue = Number(value.amountPaid) || 0
                  const paymentItem: any = { refItemId: value.refItemId, refItemType: value.refItemType, amountPaid: itemValue,amountPaidDF:value.amountPaidDF,amountPaidCF:value.amountPaidCF }
                  orderPaymentItems.push(paymentItem)
                  amountPaidSum += itemValue
              })
              newOrderPaymentChannels.push({
                paymentChannelId: 8,
                amountPaid: amountPaidSum,
                bankId: '',
                cardTypeId: '',
                orderPaymentItems,
                patientTreatmentRightId: null,
                promotionPaid: oldPaid?.amountPaid || 0,
                promotionBranchId: p.promotionBranchId,
                promotionId: p.promotionId || 0,
                voucherPaid: 0,
                voucherListId: 0,
                promotionUsage: usage,
              })
            } else {
              const orderPaymentItems: any = []
              let amountPaidSum = 0
              const itemChannel = p.itemChannel ?? {}
              const usage = p?.benefitUsage || p?.promotionUsage || 'ALL'
              Object.keys(itemChannel).forEach((itemKey: any) => {
                const item = itemChannel[itemKey]
                if (item?.checked && paid[itemKey]) {
                  const { id, type } = paid[itemKey]
                  const itemValue = Number(item.value) || 0
                  const paymentItem: any = { refItemId: id, refItemType: type, amountPaid: itemValue }
                  if (['OPERATIVE', 'OPERATIVEDF', 'OPERATIVECF'].includes(usage)) {
                    paymentItem.amountPaidDF = Number(item.dfAmount) || 0
                    paymentItem.amountPaidCF = Number(item.cfAmount) || 0
                  }
                  orderPaymentItems.push(paymentItem)
                  amountPaidSum += itemValue
                }
              })
              newOrderPaymentChannels.push({
                paymentChannelId: 8,
                amountPaid: amountPaidSum,
                bankId: '',
                cardTypeId: '',
                orderPaymentItems,
                patientTreatmentRightId: null,
                promotionPaid: p?.amountPaid || 0,
                promotionBranchId: p.promotionBranchId,
                promotionId: p.promotionId || 0,
                voucherPaid: 0,
                voucherListId: 0,
                promotionUsage: usage,
              })

            }




          })
        }
        else {
          if (channelRequiredBank.includes(channel.key)) {
            if (!bank?.bankId) {
              hasValidationError = true
              notiError(t('PAYMENT.FORM.PLS_SELECT_BANK'), undefined, undefined, () => { setDisabledButton(false) })
              return
            }
          }
          if (channelRequiredCardType.includes(channel.key)) {
            if (!bank?.cardTypeId) {
              hasValidationError = true
              notiError(t('PAYMENT.FORM.PLS_SELECT_CARD_TYPE'), undefined, undefined, () => { setDisabledButton(false) })
              return
            }
          }
          Object.keys(paidItems.itemChannel).forEach((itemKey: any) => {
            const item: any = paidItems.itemChannel[itemKey]
            if (item.checked) {
              const { id, type } = paid[itemKey]
              const itemValue = Number(item.value) || 0

              orderPaymentItems.push({
                refItemId: id,
                refItemType: type,
                amountPaid: itemValue
              })
              amountPaid += itemValue
              checkSelect = true
            }
          })

          if (checkSelect && Number(paymentChannelId) !== 7 && Number(paymentChannelId) !== 8) {
            newOrderPaymentChannels.push({
              paymentChannelId: paymentChannelId,
              amountPaid,
              bankId: bank?.bankId || '',
              cardTypeId: bank?.cardTypeId || '',
              orderPaymentItems: orderPaymentItems,
              patientTreatmentRightId: null,
              promotionPaid: 0,
              promotionBranchId: 0,
              promotionId: 0,
              voucherPaid: 0,
              voucherListId: 0,
            })
          }
        }

      }
    })

    // ถ้ามี validation error ให้หยุดการทำงาน
    if (hasValidationError) {
      return
    }

    if (props.systemConfig.menu === 'PAYMENT' || props.systemConfig.menu === 'RETROSPECTIVE' || (props.systemConfig.menu === 'EDIT_PAYMENT' && props.detail?.stage === 1)) {

      treatments?.summeryTreatments?.forEach((item: any) => {
        if (item?.voucherListId && !newOrderPaymentChannels.some((info: any) => info?.voucherListId === item?.voucherListId)) {
          const voucherPaid = Number(item?.dfVoucherDiscountAmount || 0) + Number(item?.cfVoucherDiscountAmount || 0)

          const type = item.orderTreatmentId ? 'ORDER_TREATMENT' : item.tempOrderTreatmentId ? 'TEMP_ORDER_TREATMENT' : item.tempOrderProductId ? 'TEMP_ORDER_PRODUCT' : item.orderProductId ? 'ORDER_PRODUCT' : ''
          const refId = item.orderTreatmentId || item.tempOrderTreatmentId || item.tempOrderProductId || item.orderProductId

          newOrderPaymentChannels?.push({
            paymentChannelId: 7,
            amountPaid: voucherPaid,
            voucherPaid,
            voucherListId: item?.voucherListId,
            bankId: '',
            cardTypeId: '',
            patientTreatmentRightId: item?.voucherListId,
            orderPaymentItems: [{
              refItemId: refId,
              refItemType: type,
              amountPaid: voucherPaid,
              amountPaidDF: Number(item?.dfVoucherDiscountAmount || 0),
              amountPaidCF: Number(item?.cfVoucherDiscountAmount || 0),
            }]
          })
        }
      })
    }

    if (props.systemConfig.menu === 'PAYMENT' || props.systemConfig.menu === 'RETROSPECTIVE') {

      treatments?.summeryTreatments?.forEach((item: any) => {
        item?.promotion?.length && item?.promotion.forEach((i: any) => {
          if (!i?.promotionBranchId) return

          const type = item.orderTreatmentId ? 'ORDER_TREATMENT' : item.tempOrderTreatmentId ? 'TEMP_ORDER_TREATMENT' : item.tempOrderProductId ? 'TEMP_ORDER_PRODUCT' : item.orderProductId ? 'ORDER_PRODUCT' : ''
          const refId = item.orderTreatmentId || item.tempOrderTreatmentId || item.tempOrderProductId || item.orderProductId

          // Dedupe per (branchId, refItem) so the same promotion applied to N items
          // produces N channels — not collapsed onto whichever item was iterated first.
          const alreadyPushed = newOrderPaymentChannels.some((info: any) =>
            info?.promotionBranchId === i?.promotionBranchId &&
            info?.orderPaymentItems?.some((opi: any) => opi.refItemId === refId && opi.refItemType === type)
          )
          if (alreadyPushed) return

          const promotionPaid = Number(item?.dfPromotionDiscountAmount || 0) + Number(item?.cfPromotionDiscountAmount || 0)
          const promotionPaidFinal = i?.type === "EXTRA" ? 0 : promotionPaid

          newOrderPaymentChannels?.push({
            paymentChannelId: 8,
            amountPaid: promotionPaidFinal,
            promotionPaid: promotionPaidFinal,
            promotionBranchId: i?.promotionBranchId,
            promotionId: i?.promotionId,
            bankId: '',
            cardTypeId: '',
            patientTreatmentRightId: null,
            orderPaymentItems: [{
              refItemId: refId,
              refItemType: type,
              amountPaid: promotionPaidFinal,
              amountPaidDF: Number(item?.dfPromotionDiscountAmount || 0),
              amountPaidCF: Number(item?.cfPromotionDiscountAmount || 0),
            }]
          })
        })
      })
    }

    console.log('newOrderPaymentChannels', newOrderPaymentChannels)


    // คำนวณ amountPaid และ amountReceipt โดยไม่รวมช่องทาง 5 (TREATMENT_RIGHT) และไม่รวม DCC
    // amountPaid ใน channel ไม่รวม DCC อยู่แล้ว ดังนั้นไม่ต้องลบ DCC ออกอีก
    const amountPaidWithoutChannel5 = newOrderPaymentChannels
      .filter((channel: any) => channel.paymentChannelId !== 5)
      .reduce((sum: number, channel: any) => sum + (channel.amountPaid || 0), 0)

    const amountReceipt = Math.max(0, amountPaidWithoutChannel5)
    const amountPaid = Math.max(0, amountPaidWithoutChannel5 - objPayment.amountChange)
    // คำนวณ dccPaid จากผลรวมของ amountPaidDcc ใน orderPaymentItems
    const dccPaid = _.sumBy(newOrderPaymentChannels, (channel: any) => {
      return _.sumBy(channel.orderPaymentItems || [], (item: any) => item.amountPaidDcc || 0)
    })
    let paided = 0
    if (props.systemConfig.menu === 'OVERDUE' || props.systemConfig.menu === 'EDIT_PAYMENT') {
      paided = Object.values(listOrderPayment.paid).reduce((sum: number, item: any) => sum + Number(item.paid || 0), 0)
    }

    console.log('objPayment.amountChange',objPayment.amountChange)
    const objPay = {
      prefix: objPayment.prefix,
      totalPrice: (Number(objPayment?.totalPrice || 0) + Number(priceOutStanding || 0)) - dccPaid,
      amountPaid: amountPaid,
      amountReceipt: amountReceipt,
      amountChange: objPayment.amountChange,
      overdue: (objPayment.overdue),
      remark: objPayment.remark,
      orderPaymentChannels: newOrderPaymentChannels
    }



    if (props.systemConfig.menu === 'PAYMENT') {
      const findTry = await TreatmentSummaryApi.findTreatmentSummary(props.patient.patientCase.patientCaseId).catch((res) => {
        return
      })
      if (findTry?.data?.concurrencyToken) dispatch(setConcurrencyToken(findTry.data.concurrencyToken))
      console.log(objPay)
      setLoading(true)
      await PaymentApi.payment(props.patient.patientCase.patientCaseId, objPay).then(async (resPay) => {
        if (resPay.status === 201) {
          if (resPay.data?.statusCode === 400) {
            setiIsCheckErrorNhsoModal(true)
            setCheckDataError(resPay.data?.message)
          } else {
            if (resPay.data?.statusCode === 404) {
              if (resPay.data?.message === 'PROMOTION_CAN_NOT_USE') {
                notiError(t('PAYMENT.MESSAGE.ERROR.PROMOTION_CAN_NOT_USE'))
              } else if (resPay.data?.message === 'VOUCHER_CAN_NOT_USE') {
                notiError(t('PAYMENT.MESSAGE.ERROR.VOUCHER_CAN_NOT_USE'))
              } else {
                notiError(t('PAYMENT.MESSAGE.ERROR.ERROR'))
              }
            } else {
              const pAlreadyTreatment: string = findTry.data.isFixCode === "1" ? getSecureItem('PATIENT_ALREADY_TREATMENT') || '' : ''
              const checkNewP: number[] = pAlreadyTreatment ? JSON.parse(pAlreadyTreatment) : []
              localStorage.setItem('PATIENT_ALREADY_TREATMENT', JSON.stringify(_.filter(checkNewP, (id) => id !== props.patient.patientCase.patientCaseId)))
              if (findTry.data?.summeryTreatments.length === 0) {
                if (props?.onSuccess) props.onSuccess(objPay)
                setListOrderPayment(initialListOrderPayment)
                setObjPayment(initialObjPayment)
                window.open(`${fileUrl}/${resPay.data}`, '_blank')
                notiSuccess(t('PAYMENT.MESSAGE.SUCCESS.PAY_SUCCESS'), 'reset', null, null, 'register-page')
                history.push(routeName.payment)
              } else {
                await PatientTypeApi.getPatientTypeCheckType(props.patient?.patientCase.patientCaseId).then((resp: any) => {
                  if (resp.data?.length > 0) {
                    setIsOpenAlertModal(true)
                    setDataUrl(resPay?.data)
                    setCheckType(resp.data)
                    setObjectData(objPay)
                  } else {
                    if (props?.onSuccess) props.onSuccess(objPay)
                    window.open(`${fileUrl}/${resPay.data}`, '_blank')
                    notiSuccess(t('PAYMENT.MESSAGE.SUCCESS.PAY_SUCCESS'), 'reset', null, null, 'register-page')
                    history.push(routeName.payment)
                  }
                })
              }
            }

          }

        }

        setLoading(false)

      }).catch(async (res) => {
        const resMessage = res?.response?.data?.message || ''
        const errorPromotionVoucher = ["NOT_FOUND_VOUCHER_CODE", "VOUCHER_CAN_NOT_USE", "PROMOTION_CAN_NOT_USE", "NOT_FOUND_PROMOTION"]

        if (res?.response?.data?.message === "THE_TREATMENT_LIST_HAS_BEEN_UPDATED") {
          notiError(t(`ค่ารักษามีการเปลี่ยนแปลง โปรดตรวจสอบรายการอีกครั้ง`), 'reload')
          setLoading(false)
          return
        }
        if (res?.response?.data?.message === "BRANCH_ID_NOT_MATCHED") {
          notiError(t(`ไม่สามารถชำระเงินได้ กรุณาใช้งานแค่สาขาเดียวเท่านั้น`))
          setLoading(false)
          return
        }
        if (res?.response?.data?.code === 'PROMOTION_EXCEEDS_VISIT_LIMIT') {
          const { promotionName, maxUsagePerVisit } = res.response.data
          notiError(t('PROMOTION.FORM.PROMOTION_EXCEEDS_VISIT_LIMIT', { name: promotionName, max: maxUsagePerVisit }))
          setLoading(false)
          return
        }
        if (resMessage === "PRODUCT_NOT_ENOUGH" || errorPromotionVoucher.includes(resMessage)) {
          notiError(t(`PAYMENT.MESSAGE.ERROR.${resMessage}`))
          setLoading(false)
          return
        }

        const resError = await handelCheckQueue()
        if (!resError) {
          notiError(t('PAYMENT.MESSAGE.ERROR.VISIT_DONE'))
          setLoading(false)
          return
        } else {
          notiError(t('PAYMENT.MESSAGE.ERROR.ERROR'))
        }
        // history.push(routeName.payment)
        setLoading(false)
        return
      }).finally(() => {
        setDisabledButton(false)
      })

    } else if (props.systemConfig.menu === 'RETROSPECTIVE') {
      setDisabledButton(false)
      return props.onSuccess && props.onSuccess(objPay)
    } else if (props.systemConfig.menu === 'EDIT_PAYMENT') {
      setLoading(true)
      PaymentApi.updateDetail(props.detail.orderPaymentId, objPay).then((resPay) => {
        setLoading(false)
        if (resPay.status === 200) {
          window.open(`${fileUrl}/${resPay.data}`, '_blank')
          notiSuccess(t('PAYMENT.MESSAGE.SUCCESS.PAY_SUCCESS'), 'reset', null, null, 'register-page')
        }
        if (props?.onSuccess) props.onSuccess()
      }).catch((res) => {
        setLoading(false)
        setDisabledButton(false)
        const msg = res?.response?.data?.message
        if (msg === 'ADVANCE_REFUND_NOT_ENOUGH') {
          notiError(t('PAYMENT.MESSAGE.ERROR.ADVANCE_REFUND_NOT_ENOUGH'))
          return
        }
        notiError(t('PAYMENT.MESSAGE.ERROR.ERROR'))
      }).finally(() => {
        setDisabledButton(false)
      })
    } else {
      if (props?.orderPaymentId) {
        if (Number(objPayment.overdue) !== 0) {
          if (!newOrderPaymentChannels.length) {
            setDisabledButton(false)
            notiError(t('PAYMENT.FORM.PLS_INPUT_PAYMENT'))
          }
        }
        setLoading(true)
        // Open the receipt tab now, while this still counts as the user's click.
        // The overdue receipt PDF is built before the API answers, which can
        // outlast the browser's user-activation window, and a window.open made
        // after that is silently blocked (MD-2609-1117).
        const receiptWindow = window.open('', '_blank')
        OverdueApi.paidOverdue(props?.orderPaymentId, objPay).then((resPay) => {
          // Same shape as the normal payment path: clear the overlay, open the
          // receipt and refresh the list right away.
          setLoading(false)
          setDisabledButton(false)
          if (resPay.status === 201) {
            if (receiptWindow) receiptWindow.location.href = `${fileUrl}/${resPay.data}`
            else window.open(`${fileUrl}/${resPay.data}`, '_blank')
            if (props?.onSuccess) props.onSuccess()
            notiSuccess(t(`PAYMENT.MESSAGE.SUCCESS.PAY_OVERDUE_SUCCESS`), 'reset', null, null, 'register-page')
          } else receiptWindow?.close()
        }).catch(async (res) => {
          receiptWindow?.close()
          setLoading(false)
          setDisabledButton(false)
          return
        }).finally(() => {
          return
        })
      }
    }

  }



  const payment = async () => {
    const overdueCal = (objPayment.overdue - objPayment.approvedArrear) || 0
    const isCreatingOverdue = overdueCal > 0;
    const isPartialPayment = overdueCal !== objPayment.totalPrice || overdueCal === objPayment.totalPrice;

    const { treatmentRight } = listOrderPayment
    const checkSelected = Object.values(selectdChannel).filter((item: any) => item.isActive)
    if (!checkSelected.length) return notiError(t('PAYMENT.FORM.PLS_SELECT_PAYMENT_CHANNEL'), undefined, undefined, () => {
      setDisabledButton(false)
      setDisabledNext(false)
    })
    else if (overdueCal === 0 && treatmentRight && selectdChannel[5].isActive && props.systemConfig.menu !== 'EDIT_PAYMENT' && props.systemConfig.isUseNhso) return notiError(t('PAYMENT.MESSAGE.SUCCESS.CHANGE_PAYMENT_METHOD'), undefined, undefined, () => {
      setDisabledButton(false)
      setDisabledNext(false)
    })
    else if (isCreatingOverdue && isPartialPayment && props.outStandingPayment === '1') {
      setConfigModal({
        modul: 'overdue-error',
        title: t('ไม่สามารถค้างชำระได้'),
        message: t('ไม่สามารถค้างชำระได้ กรุณาทำรายการใหม่อีกครั้ง'),
        onSubmit: onCloseConfigModal(),
        cancel: onCloseConfigModal()
      })
      setOpenModal(true)
    }
    else if (isCreatingOverdue && treatments?.overdueStatus !== ApprovalStatus.APPROVED && props.outStandingPayment === '1') {
      return notiError(t('ไม่สามารถสร้างยอดค้างชำระได้เนื่องจากยังไม่ได้รับการอนุมัติ'), undefined, undefined, () => {
        setDisabledButton(false);
        setDisabledNext(false);
      });
    }
    else if (overdueCal + priceOutStanding > 0 && props.systemConfig.menu !== 'EDIT_PAYMENT') {
      const optionButton = {
        confirmButton: {
          confirmButtonText: t('PAYMENT.FORM_PAYMENT.OVERDUE_CONFIRM.YES')
        },
        cancelButton: {
          cancelButtonText: t('PAYMENT.FORM_PAYMENT.OVERDUE_CONFIRM.NO'),
          showCancelButton: true
        },
      }
      swalCustom(
        t('PAYMENT.FORM_PAYMENT.OVERDUE_CONFIRM.TITLE'),
        t('PAYMENT.FORM_PAYMENT.OVERDUE_CONFIRM.CAPTION'), (res: any) => {
          if (res) {
            setDisabledButton(true)
            setDisabledNext(true)
            if (!checkSelected.length) return notiError(t('PAYMENT.FORM.PLS_SELECT_PAYMENT_CHANNEL'), undefined, undefined, () => {
              setDisabledButton(false)
              setDisabledNext(false)
            })
            else if (isUseCrm) handelCheckRank()
            else finishPayment()
          } else {
            setDisabledButton(false)
            setDisabledNext(false)
          }
        },
        'question',
        optionButton
      )
    } else if (isUseCrm) handelCheckRank()
    else finishPayment()
  }

  const handelCheckRank = async () => {
    const resRank = await RegisterApi.ranksCheck({ patientId: props.patient.patientId, payment: autoIncrement === 'VISIT_COUNT' ? 0 : objPayment.amountReceipt, autoIncrement: autoIncrement });
    const currentRank = _.find(memberAll, { rankId: props.patient?.rankId })
    const currentIndex = _.findIndex(memberAll, { rankId: props.patient?.rankId })
    const newIndex = _.findIndex(memberAll, { rankId: resRank.data?.rankId })
    if (currentRank?.rankId !== resRank.data?.rankId) {
      if (props.patient.keyAction === '0' || (props.patient.keyAction === '1' && currentIndex < newIndex)) {
        setNewRank(resRank.data);
        setCurrentRank(currentRank);
        setIsDupicate(true);
        setDisabledButton(false)
      } else finishPayment()
    } else finishPayment()
  }

  const handelFinishPayment = async () => {
    setIsDupicate(false)
    finishPayment()
  }
  const onCloseConfigModal = async () => {
    setDisabledButton(false)
    setDisabledNext(false)
    setOpenModal(false)
  }
  const handelCheckQueue = async () => {
    let check = false
    await PaymentApi.listQueue({ branchId: branchIdMemo }).then(({ data }) => {
      check = data.queue.some((item: any) => item.patientCaseId === props.patient.patientCase.patientCaseId)
    })

    return check
  }
  const isMenu = useMemo(() => (props.systemConfig.menu), [props.systemConfig.menu])

  return (
    <UseStyled.ModalStyle size={'xl'} show={props.isShow} centered fullscreen>
      <PaymentModals
        props={{ ...props, type: 'PAYMENT' }}
        configModal={configModal}
        openModal={openModal}
        isOpenAlertModal={isOpenAlertModal}
        isNhsoSaveMedicalModal={isNhsoSaveMedicalModal}
        isCheckErrorNhsoModal={isCheckErrorNhsoModal}
        isDupicate={isDupicate}
        checkDataError={checkDataError}
        checkType={checkType}
        dataUrl={dataUrl}
        objectData={objectData}
        currentRank={currentRank}
        newRank={newRank}
        autoIncrement={autoIncrement}
        onCloseConfigModal={() => onCloseConfigModal()}
        onCloseAlertModal={() => setIsOpenAlertModal(false)}
        onCloseNhsoModal={() => setIsNhsoSaveMedicalModal(false)}
        onCloseErrorNhsoModal={() => setiIsCheckErrorNhsoModal(false)}
        onCloseDupicateModal={() => setIsDupicate(false)}
        onConfirmNhso={() => setIsNhsoSaveMedicalModal(true)}
        onConfirmRankChange={() => handelFinishPayment()}
      />
      <Modal.Body className="py-0 overflow-hidden">
        <Loading show={loading} type="fullLoading" />
        <Grid container spacing={2} className="my-0 h-100 pb-4">
          <Grid lg={6} item className={`w-100 h-100 ${activePage === 0 ? 'd-block' : 'd-none'} d-xl-block`}>
            <Box className="pr-xl-3 h-100 d-flex flex-column">
              <Typography className="pb-4 text-center" sx={{ fontSize: 20, color: colors.themeMainColor, fontWeight: 600 }}>
                {t('PAYMENT.FORM_PAYMENT.TITLE.PAYMENT_DETAIL')}
              </Typography>
              <Box className="custom-scroll" sx={{ overflowX: 'auto' }}>
                <Info patient={props.patient} finance={{ advanceBalance: patientFinance.advanceBalance, outstandingBalance: patientFinance.outstandingBalance }} treatmentRights={patientFinance.treatmentRights} />
                <PaymentChannels
                  systemConfig={{ ...props.systemConfig, isShow: props.isShow }}
                  objPayment={objPayment}
                  bankTransfers={bankTransfers}
                  bankCredits={bankCredits}
                  bankDebits={bankDebits}
                  listOrder={sortedListOrder}
                  selectdChannel={selectdChannel}
                  setSelectdChannel={setSelectdChannel}
                  setObjPayment={setObjPayment}
                  listOrderPayment={listOrderPayment}
                  setListOrderPayment={(value: any) => setListOrderPayment((prev: any) => ({ ...prev, ...value }))}
                  advanceBalance={patientFinance.advanceBalance}
                  treatmentRights={patientFinance.treatmentRights}
                  promotionList={promotionList}
                  voucherList={voucherList}
                  dataPatientCase={props.dataPatientCase}
                  patientId={props.patient?.patientId}
                  date={props.date}
                />
              </Box>
              <UseStyled.FooterStepButton className="d-flex d-xl-none justify-content-md-end mt-2 mr-md-2">
                <ButtonCustom textButton={t('BUTTON.CANCEL')} variant="outlined" onClick={() => props.setIsShow(false)} />
                <ButtonCustom textButton={t('BUTTON.NEXT')}
                  disabled={loading}
                  onClick={() => {
                    setDisabledNext(true)
                    alertTreantRights()
                  }}
                />
              </UseStyled.FooterStepButton>
            </Box>
          </Grid>
          <Grid lg={6} item className={`w-100 h-100 ${activePage === 1 ? 'd-block' : 'd-none'} d-xl-block`} sx={{ borderLeft: { lg: `1px solid ${colors.lightGray}` } }}>
            <Box className="h-100 d-flex flex-column">
              <Typography className="pb-4 text-center" sx={{ fontSize: 20, color: colors.themeMainColor, fontWeight: 600 }}>
                {t('PAYMENT.FORM_PAYMENT.TITLE.PAYMENT_SUMMARY')}
              </Typography>
              <Box className="custom-scroll mb-3" sx={{ overflowX: 'auto' }}>
                <UseStyled.SummaryNetPrice className="d-flex align-items-center mx-md-3 mx-xl-4 mb-3">
                  <Box className="d-flex align-items-center justify-content-center flex-column h-100">
                    <Typography className="text-center" sx={{ fontSize: 20, color: colors.themeMainColor, fontWeight: 600 }}>
                      {numberFormat(objPayment.totalPrice + objPayment.approvedArrear)} {t('CURRENCY_CHANGE.TH')}
                    </Typography>
                    <Typography className="text-center" sx={{ fontSize: 14, color: colors.themeMainColor }}>
                      {t('PAYMENT.FORM_PAYMENT.NET_PRICE')}
                    </Typography>
                  </Box>
                  <Box className="d-flex align-items-center justify-content-center flex-column h-100" sx={{ borderLeft: `1px solid ${colors.lightGray}` }}>
                    <Typography className="text-center" sx={{ fontSize: 20, color: colors.themeMainColor, fontWeight: 600 }}>
                      {numberFormat(objPayment.overdue)} {t('CURRENCY_CHANGE.TH')}
                    </Typography>
                    <Typography className="text-center" sx={{ fontSize: 14, color: colors.themeMainColor }}>
                      {t('PAYMENT.FORM_PAYMENT.OUT_STANDING_PRICE')}
                    </Typography>
                  </Box>
                </UseStyled.SummaryNetPrice>
                <Box className="d-flex align-items-center px-md-2 px-xl-3 mb-3">
                  <Typography sx={{ fontSize: 18, fontWeight: 500 }}>{t('PAYMENT.FORM_PAYMENT.RECEIPT_TYPE')}</Typography>
                  <Box className="d-flex align-items-center justify-content-end pl-2" sx={{ flex: 1 }}>
                    <Box className="w-100" sx={{ maxWidth: '112px' }}>

                      <BasicSelect
                        disabled={Config.IS_TDH === '1' && isMenu === 'EDIT_PAYMENT'}
                        selectId=""
                        label=""
                        labelId="billPrefixId"
                        onchange={(e: any) => setObjPayment((prev: any) => ({ ...prev, prefix: e.target.value }))}
                        value={objPayment.prefix}
                        options={_.map(billPrefixes, (i: any) => (
                          <MenuItem key={i.billPrefixId || i.paymentChannelId} value={i.prefix || i.defaultPrefix}>
                            {i.prefix || i.defaultPrefix}
                          </MenuItem>
                        ))}
                      />
                    </Box>
                    <Typography className="pl-3 d-none d-sm-block" sx={{ flex: 'none' }}>
                      {isMenu !== 'RETROSPECTIVE' ? '-xxx-xx-xx-xxxxx' : '-xx-xx-xxxxx'}
                    </Typography>
                  </Box>
                </Box>
                <SummaryReceipt
                  systemConfig={{
                    menu: isMenu,
                    isUseCrm: clinicInfo.isUseCrm === '1',
                    isUseNhso: props.dataPatientCase?.isUseNhso === '1',
                    patientCaseId: props.dataPatientCase?.patientCaseId
                  }}
                  objPayment={objPayment}
                  listOrder={sortedListOrder}
                  listOrderPayment={listOrderPayment}
                  initBalance={props.initBalance}
                  patient={props.patient} date={props.date}
                />
                <Box className="px-md-2 px-xl-3 mb-md-3 pt-md-2">
                  <InputTextarea
                    key={'important-symptoms'}
                    onchange={(e: any) => setObjPayment((prev: any) => ({ ...prev, remark: e.target.value }))}
                    value={objPayment.remark}
                    label={t('PAYMENT.FORM_PAYMENT.REMARK')}
                  />
                </Box>
                <Box className="px-md-2 px-xl-3 mb-md-3 pt-md-2">
                  {props.histories?.length && (
                    <>
                      {_.map(props.histories, (his: any, iHis: number) => {
                        return <p key={his.orderPaymentId || iHis} className="mb-1">{`${t('PAYMENT.PAY')} #${iHis + 1} ${dateTimeToView(his.createdAt)} (${numberToCurrencyFormat(his.amountItemPaid)})`}</p>
                      })}
                    </>
                  )}
                </Box>
              </Box>
              <UseStyled.FooterPaymentButton className="d-flex flex-column mt-auto">
                <ButtonCustom textButton={t('PAYMENT.FORM_PAYMENT.BUTTON_BACK_TO_DETAIL')} variant="outlined" onClick={() => {
                  setActivePage(0)
                  setDisabledNext(false)
                }} className="d-xl-none" />
                <ButtonCustom
                  textButton={t('BUTTON.CANCEL')}
                  variant="outlined"
                  onClick={() => {
                    props.setIsShow(false)
                  }}
                  className="d-none d-xl-block"
                />
                <ButtonCustom
                  textButton={t('PAYMENT.FORM_PAYMENT.BUTTON_PAYMENT')}
                  disabled={loading || disabledButton}
                  onClick={() => {
                    if (props.systemConfig.isUseNhso) {
                      setIsNhsoSaveMedicalModal(true)
                    } else {
                      setDisabledButton(true)
                      payment()
                    }
                  }}
                  className="mt-2 mt-md-3"
                />
              </UseStyled.FooterPaymentButton>
            </Box>
          </Grid>
        </Grid>
      </Modal.Body>
    </UseStyled.ModalStyle>
  )
}
